import { useRef } from "react";
import { Sparkles, Activity, Zap, Target, Flame, Scissors, Shield, ArrowLeft, ArrowRight } from "lucide-react";
import { motion, useInView } from "framer-motion";
import { Card, CardContent } from "../ui/card";
import { useLanguage } from "../../contexts/LanguageContext";
import { SectionCTA } from "../shared/SectionCTA";

const treatmentIcons = [Sparkles, Activity, Zap, Target, Flame, Scissors, Shield];

export const TreatmentsSection = () => {
  const { language, direction, translations: t } = useLanguage();
  const ArrowIcon = direction === 'rtl' ? ArrowLeft : ArrowRight;
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  return (
    <section id="treatments" className="py-20 md:py-28 bg-white scroll-mt-20" data-testid="treatments-section" ref={ref}>
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <motion.div
          className="text-center mb-16"
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
        >
          <span className="text-[#C4956A] font-semibold mb-2 block">{t.treatments.label}</span>
          <h2 className="font-['Heebo'] text-3xl md:text-4xl lg:text-5xl font-bold text-[#2C2C2C] mb-4">
            {t.treatments.title}
          </h2>
          <p className="text-lg text-[#2C2C2C]/70 max-w-2xl mx-auto">{t.treatments.subtitle}</p>
          <div className="w-20 h-1 bg-[#C4956A] mx-auto mt-4" />
        </motion.div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {t.treatments.items.map((treatment, i) => {
            const Icon = treatmentIcons[i];
            return (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 30 }}
                animate={isInView ? { opacity: 1, y: 0 } : {}}
                transition={{ delay: i * 0.1 }}
              >
                <Card className="h-full bg-[#FAF7F2] border-0 rounded-2xl overflow-hidden group cursor-pointer hover:-translate-y-1 hover:shadow-xl transition-all duration-300" data-testid={`treatment-card-${i+1}`}>
                  <CardContent className="p-8">
                    <div className="w-16 h-16 rounded-2xl bg-[#C4956A]/10 flex items-center justify-center mb-6 group-hover:bg-[#C4956A]/20 transition-colors">
                      <Icon className="w-8 h-8 text-[#C4956A]" />
                    </div>
                    <span className="text-xs text-[#C4956A] font-medium bg-[#C4956A]/10 px-3 py-1 rounded-full">{treatment.tag}</span>
                    <h3 className="font-['Heebo'] text-xl font-bold text-[#2C2C2C] mb-3 mt-3">{treatment.title}</h3>
                    <p className="text-[#2C2C2C]/70 mb-4">{treatment.description}</p>
                    <a href="#contact" className="text-[#C4956A] font-semibold inline-flex items-center group-hover:gap-2 transition-all">
                      {t.treatments.moreInfo}
                      <ArrowIcon className="w-4 h-4 mx-1" />
                    </a>
                  </CardContent>
                </Card>
              </motion.div>
            );
          })}
        </div>

        <SectionCTA text={t.treatments.sectionCta} buttonText={t.treatments.sectionCtaButton} />
      </div>
    </section>
  );
};
